import { Card, Col, DatePicker, Form, Input, Row, Typography } from "antd";
import dayjs from 'dayjs';
import React, { useMemo, useCallback } from "react";
import { validateEndDate } from "../dateValidator";
import { useTranslation } from "react-i18next";

const { Title } = Typography;

const Facility = ({ patientDetailsFMLA, getChildData }) => {
  const { t } = useTranslation();
  const callparent = (e, name) => {
    getChildData(e, name);
  };
  const dateFormat = "MM/DD/YYYY";
  const disableFutureDates = (current) => {
    // Can not select days after today
    return current && current > dayjs().endOf('day');
  };

  const disablePastDates = (current) => {
    return patientDetailsFMLA.FacilityStartDate && patientDetailsFMLA.FacilityStartDate > current;
  };
  
  const validatePhone = useCallback((_, value) => {
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    if (!value || phoneRegex.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(t("common:please_enter_a_valid_phone_number")));
  }, [t]);
  
  const validationRules = useMemo(() => ({
    startDate: [{ required: true, message: t("patientDetailsFMLS:please_enter_start_date") }],
    endDate: [{ required: true, message: t("patientDetailsFMLS:please_enter_end_date") }, { validator: () => validateEndDate(patientDetailsFMLA.FacilityStartDate, patientDetailsFMLA.FacilityEndDate, t("common:end_date_must_be_greater_than_start_date")) }],
    facilityName: [{ required: true, max: 100, message: t("patientDetailsFMLS:please_enter_facility_name") }],
    email: [{ max: 100, message: t("patientDetailsFMLS:please_enter_facility_email") }, { type: "email", message: t("patientDetailsFMLS:please_enter_valid_email") }],
    phone: [{ max: 20, message: t("patientDetailsFMLS:please_enter_phone_number") }, { validator: validatePhone }],
    address: [{ required: true, max: 150, message: t("patientDetailsFMLS:please_enter_address") }],
    city: [{ required: true, max: 50, message: t("patientDetailsFMLS:please_enter_city_name") }],
    state: [{ required: true, message: t("patientDetailsFMLS:please_enter_state_name") }],
    zipCode: [{ max: 8, required: true, message: t("patientDetailsFMLS:please_enter_zip_code") }, { pattern: /^[0-9]+$/, message: t("patientDetailsFMLS:zip_code_must_be_numeric") }, { min: 5, message: t("patientDetailsFMLS:zip_code_must_be_at_least_5_digits_long") }]
  }), [t, validatePhone, patientDetailsFMLA.FacilityStartDate, patientDetailsFMLA.FacilityEndDate]);

  return (
    <Card
      styles={{ body: { padding: 0 }, header: { marginBottom: 20 } }}
      bordered={false}
      size="0"
      title={
        <Title
          className="font-semibold"
          style={{ marginBottom: "0" }}
          level={4}
        >
          {t("patientDetailsFMLS:facility_information")}
        </Title>
      }
    >
      <Row gutter={[20, 0]} className="px-1 md:px-6">
        <Col span={24}>
          <label className="font-medium text-[0.813rem] mb-4 flex">{t("patientDetailsFMLS:what_dates_are_you_requesting_leave")}</label>
        </Col>
        <Col xs={20} md={10}>
          <Form.Item
            name="FacilityStartDate"
            label={t("patientDetailsFMLS:start_date")}
            required
            rules={validationRules.startDate}
          >
            <DatePicker
              format={dateFormat}
              // disabledDate={disableFutureDates}
              style={{ minWidth: "100%" }}
              name="FacilityStartDate"
              onChange={(e) => callparent(e, "FacilityStartDate")}
              placeholder={t("common:select_date")}
            />
          </Form.Item></Col>
        <Col xs={24} md={14}></Col>
        <Col xs={20} md={10}>
          <Form.Item
            name="FacilityEndDate"
            label={t("patientDetailsFMLS:end_date")}
            required
            rules={validationRules.endDate}
          >
            <DatePicker
              format={dateFormat}
              disabledDate={disablePastDates}
              style={{ minWidth: "100%" }}
              name="FacilityEndDate"
              onChange={(e) => callparent(e, "FacilityEndDate")}
              placeholder={t("common:select_date")}
            />
          </Form.Item></Col>
        <Col span={24}>
          <label className="font-medium text-[0.813rem] mb-4 flex">{t("patientDetailsFMLS:at_which_facility_did_the_patient_receive_care")}</label>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            className="font-medium"
            name="ffacilityname"
            label={t("patientDetailsFMLS:facility_name")}
            onChange={(e) => callparent(e.target.value, "ffacilityname")}
            rules={validationRules.facilityName}
          >
            <Input maxLength={100} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            className="font-medium"
            name="FphysicianName"
            label={t("patientDetailsFMLS:physician_name")}
            onChange={(e) => callparent(e.target.value, "FphysicianName")}
            rules={[{ required: false, max: 100, message: t("patientDetailsFMLS:please_enter_physician_name") }]}
          >
            <Input maxLength={100} />
          </Form.Item></Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t("patientDetailsFMLS:facility_email")}
            name="FacilityEmail"
            onChange={(e) => callparent(e.target.value, "FacilityEmail")}
            rules={validationRules.email}
          >
            <Input maxLength={100} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="fphone"
            className="font-medium"
            label={t("patientDetailsFMLS:facility_phone_number")}
            onChange={(e) => callparent(e.target.value, "FacilityPhoneNumber")}
            rules={validationRules.phone}
          >
            <Input maxLength={20} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="faddress1"
            label={t("patientDetailsFMLS:address_line_1")}
            onChange={(e) => callparent(e.target.value, "FacilityAddressLine1")}
            rules={validationRules.address}
          >
            <Input maxLength={150} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="faddress2"
            className="font-medium"
            label={t("patientDetailsFMLS:address_line_2")}
            onChange={(e) => callparent(e.target.value, "FacilityAddressLine2")}
          >
            <Input maxLength={150} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="fcity"
            label={t("patientDetailsFMLS:city")}
            onChange={(e) => callparent(e.target.value, "FacilityCity")}
            rules={validationRules.city}
          >
            <Input maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="fstate"
            label={t("patientDetailsFMLS:state")}
            onChange={(e) => callparent(e.target.value, "FacilityState")}
            rules={validationRules.state}
          >
            <Input maxLength={50} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="fzipcode"
            label={t("patientDetailsFMLS:zip_code")}
            onChange={(e) => callparent(e.target.value, "FacilityZipCode")}
            rules={validationRules.zipCode}
          >
            <Input maxLength={8} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};
export default Facility;
